import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Calendar, Clock, MapPin, Phone, Mail, ArrowRight, Home, CalendarPlus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { bookingsAPI } from '../services/api';

// Helper function to format date for calendar
const formatDateForCalendar = (dateStr, timeStr) => {
  const date = new Date(dateStr);
  // Parse time like "10:00 AM" or "2:30 PM"
  const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (timeMatch) {
    let hours = parseInt(timeMatch[1]);
    const minutes = parseInt(timeMatch[2]);
    const period = timeMatch[3].toUpperCase();
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    date.setHours(hours, minutes, 0, 0);
  }
  return date;
};

// Generate Google Calendar URL
const generateGoogleCalendarUrl = (booking) => {
  const startDate = formatDateForCalendar(booking.scheduled_date, booking.scheduled_time);
  const endDate = new Date(startDate.getTime() + (booking.duration_hours * 60 * 60 * 1000));
  
  const formatGoogleDate = (date) => {
    return date.toISOString().replace(/-|:|\.\d{3}/g, '');
  };
  
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `NC Help Hub - ${booking.service_name}`,
    dates: `${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}`,
    details: `Booking ID: ${booking.id.slice(0, 8).toUpperCase()}\nService: ${booking.service_name}\nDuration: ${booking.duration_hours} hours\nTotal: $${booking.total_amount?.toFixed(2) || '0.00'}\n\nBooked via NC Help Hub - nchelphub.com`,
    location: `${booking.address}, ${booking.city}, ${booking.state} ${booking.zip_code}`,
    sf: 'true'
  });
  
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

// Generate Apple Calendar / iCal file
const generateICalFile = (booking) => {
  const startDate = formatDateForCalendar(booking.scheduled_date, booking.scheduled_time);
  const endDate = new Date(startDate.getTime() + (booking.duration_hours * 60 * 60 * 1000));
  
  const formatICalDate = (date) => {
    return date.toISOString().replace(/-|:|\.\d{3}/g, '').slice(0, -1);
  };
  
  const icalContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//NC Help Hub//Booking//EN',
    'BEGIN:VEVENT',
    `UID:${booking.id}@nchelphub.com`,
    `DTSTAMP:${formatICalDate(new Date())}`,
    `DTSTART:${formatICalDate(startDate)}`,
    `DTEND:${formatICalDate(endDate)}`,
    `SUMMARY:NC Help Hub - ${booking.service_name}`,
    `DESCRIPTION:Booking ID: ${booking.id.slice(0, 8).toUpperCase()}\\nService: ${booking.service_name}\\nDuration: ${booking.duration_hours} hours\\nTotal: $${booking.total_amount?.toFixed(2) || '0.00'}\\n\\nBooked via NC Help Hub`,
    `LOCATION:${booking.address}\\, ${booking.city}\\, ${booking.state} ${booking.zip_code}`,
    'STATUS:CONFIRMED',
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');
  
  return icalContent;
};

const downloadICalFile = (booking) => {
  const icalContent = generateICalFile(booking);
  const blob = new Blob([icalContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `nchelphub-booking-${booking.id.slice(0, 8)}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const BookingConfirmation = () => {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        // Use public confirmation endpoint (no auth required)
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings/confirmation/${bookingId}`);
        if (response.ok) {
          const data = await response.json();
          setBooking(data);
        }
      } catch (error) {
        console.error('Failed to fetch booking:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [bookingId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--background)]">
        <div className="animate-pulse text-[var(--primary)]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--background)] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="card p-8 text-center"
        >
          {/* Success Icon */}
          <div className="w-20 h-20 bg-[var(--success)]/15 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-[var(--success)]" />
          </div>

          <h1 className="text-3xl font-bold mb-2 font-['Cormorant_Garamond']">
            Booking Confirmed!
          </h1>
          <p className="text-[var(--text-muted)] mb-8">
            Thank you for your booking. We'll contact you shortly to confirm your appointment.
          </p>

          {booking && (
            <div className="bg-[var(--background-subtle)] rounded-xl p-6 text-left mb-8">
              <h2 className="font-bold text-lg mb-4">Booking Details</h2>
              
              {/* Reference Number */}
              <div className="bg-[var(--primary)]/10 border border-[var(--primary)]/30 rounded-lg p-4 mb-4 text-center">
                <p className="text-xs text-[var(--text-muted)] uppercase tracking-wider mb-1">Reference Number</p>
                <p className="text-2xl font-bold text-[var(--primary)]">{booking.reference_number || booking.id?.slice(0, 8).toUpperCase()}</p>
                <p className="text-xs text-[var(--text-muted)] mt-1">Save this for your records</p>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-[var(--primary)] mt-0.5" />
                  <div>
                    <p className="font-medium">{booking.service_name}</p>
                    <p className="text-sm text-[var(--text-muted)]">
                      {new Date(booking.scheduled_date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-[var(--primary)]" />
                  <p>{booking.scheduled_time} ({booking.duration_hours} hours)</p>
                </div>

                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[var(--primary)] mt-0.5" />
                  <div>
                    <p>{booking.address}</p>
                    <p className="text-sm text-[var(--text-muted)]">
                      {booking.city}, {booking.state} {booking.zip_code}
                    </p>
                  </div>
                </div>

                {booking.guest_email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-[var(--primary)]" />
                    <p>{booking.guest_email}</p>
                  </div>
                )}

                {booking.guest_phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-[var(--primary)]" />
                    <p>{booking.guest_phone}</p>
                  </div>
                )}

                <div className="pt-4 border-t border-[var(--border)] mt-4">
                  <div className="flex justify-between items-center text-lg font-bold">
                    <span>Total</span>
                    <span className="text-[var(--primary)]">${booking.total_amount?.toFixed(2) || '0.00'}</span>
                  </div>
                  <p className="text-sm text-[var(--text-muted)] mt-1">
                    Payment will be collected at time of service
                  </p>
                </div>

                {/* Add to Calendar Section */}
                <div className="pt-4 border-t border-[var(--border)] mt-4">
                  <p className="text-sm font-medium mb-3 flex items-center gap-2">
                    <CalendarPlus className="w-4 h-4 text-[var(--primary)]" />
                    Add to Calendar
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <a 
                      href={generateGoogleCalendarUrl(booking)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-800 rounded-lg text-sm font-medium transition-colors border border-gray-200"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Google Calendar
                    </a>
                    <button 
                      onClick={() => downloadICalFile(booking)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                      </svg>
                      Apple / Outlook
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <p className="text-sm text-[var(--text-muted)]">
              Booking Reference: <span className="font-mono text-[var(--text-primary)]">{bookingId?.slice(0, 8).toUpperCase()}</span>
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/register">
                <Button className="btn-primary gap-2">
                  Create Account
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="gap-2">
                  <Home className="w-4 h-4" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>

        {/* What's Next */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8 card p-6"
        >
          <h3 className="font-bold text-lg mb-4">What happens next?</h3>
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-[var(--primary)]/15 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-[var(--primary)]">1</span>
              </div>
              <div>
                <p className="font-medium">Confirmation Call</p>
                <p className="text-sm text-[var(--text-muted)]">We'll call you within 24 hours to confirm your booking details</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-[var(--primary)]/15 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-[var(--primary)]">2</span>
              </div>
              <div>
                <p className="font-medium">Worker Assignment</p>
                <p className="text-sm text-[var(--text-muted)]">A qualified professional will be assigned to your booking</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-[var(--primary)]/15 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-[var(--primary)]">3</span>
              </div>
              <div>
                <p className="font-medium">Service Day</p>
                <p className="text-sm text-[var(--text-muted)]">Our team will arrive at your scheduled time</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BookingConfirmation;
